import { createContext, useContext, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

type PageHeaderHost = HTMLElement | null | undefined;

const PageHeaderHostContext = createContext<PageHeaderHost>(undefined);

export function PageHeaderHostProvider({
  host,
  children,
}: {
  host: HTMLElement | null;
  children: ReactNode;
}) {
  return (
    <PageHeaderHostContext.Provider value={host}>
      {children}
    </PageHeaderHostContext.Provider>
  );
}

export function PageHeader({
  title,
  metadata,
  description,
  actions,
  tone = "default",
  className,
  titleClassName,
  children,
}: {
  title?: ReactNode;
  metadata?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  tone?: "default" | "analytics";
  className?: string;
  titleClassName?: string;
  children?: ReactNode;
}) {
  const host = useContext(PageHeaderHostContext);
  const content = (
    <header
      className={cn(
        "flex min-w-0 shrink-0 flex-col gap-3 pb-4 sm:flex-row sm:items-center sm:justify-between",
        host === undefined && "border-b border-line",
        tone === "analytics" && "atmospheric-header",
        tone === "analytics" && Boolean(host) && "-mt-4 pt-4",
        className,
      )}
    >
      {children ?? (
        <>
          <div className="min-w-0">
            {metadata ? <div className="mb-1.5">{metadata}</div> : null}
            <h1
              className={cn(
                "text-2xl font-medium tracking-[-0.04em] text-ink sm:text-3xl",
                titleClassName,
              )}
            >
              {title}
            </h1>
            {description ? (
              <div className="mt-2 max-w-2xl text-sm leading-5 text-muted">
                {description}
              </div>
            ) : null}
          </div>
          {actions ? (
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              {actions}
            </div>
          ) : null}
        </>
      )}
    </header>
  );

  if (host === null) return null;
  return host ? createPortal(content, host) : content;
}
